import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Loader2, Send, FlaskConical, ChevronDown, ChevronUp, Eye, GripVertical } from 'lucide-react';
import { NoteGrid, minutesLabel, DAYS } from './NoteGrid';
import { BoardSettingsForm } from './BoardSettingsForm';

export const BoardCard = ({ board, onChanged, dragHandle }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [frame, setFrame] = useState(0);
  const s = board.settings;
  const frames = board.preview || [];
  const run = async (what, fn, okMsg) => {
    setBusy(what);
    try { const r = await fn(); toast.success(typeof okMsg === 'function' ? okMsg(r.data) : okMsg); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    setBusy('');
  };
  const schedule = [
    s.days?.length ? s.days.map(d => DAYS[d]).join(' ') : 'täglich',
    s.window ? `${s.window.start}–${s.window.end}` : 'ganztags',
    board.kind === 'rotation' && s.exclusive ? 'exklusiv' : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className={`bg-card border rounded-sm p-4 ${s.enabled ? 'border-border' : 'border-border/50 opacity-70'}`} data-testid={`vb-board-${board.id}`}>
      <div className="flex items-start gap-3">
        {dragHandle && <span className="cursor-grab text-muted-foreground/50 hover:text-muted-foreground mt-1" data-testid={`vb-drag-${board.id}`}><GripVertical className="w-4 h-4" /></span>}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-foreground">{board.name}</p>
            <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border ${board.kind === 'rotation' ? 'text-sky-300 border-sky-500/40' : 'text-orange-300 border-orange-500/40'}`}>{board.kind === 'rotation' ? 'ROTATION' : `EVENT · P${s.priority}`}</span>
            {board.shows_deal_data && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm border border-violet-500/40 text-violet-300" title="Zeigt Projektnamen — im Besucher-Modus pausiert">DEAL-DATEN</span>}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">{board.description}</p>
          <p className="text-[10px] font-mono text-muted-foreground mt-1" data-testid={`vb-board-schedule-${board.id}`}>{minutesLabel(s.display_seconds)} · {schedule}</p>
        </div>
        <Switch checked={!!s.enabled} disabled={busy === 'enable'} data-testid={`vb-enabled-${board.id}`}
          onCheckedChange={(v) => run('enable', () => api.vbManagerSaveBoard(board.id, { enabled: v }), v ? `${board.name} aktiviert` : `${board.name} deaktiviert`)} />
      </div>
      <div className="flex items-center gap-3 mt-3 flex-wrap">
        <div className="relative">
          <NoteGrid matrix={frames[frame]?.matrix} size="md" testid={`vb-preview-${board.id}`} />
          {frames.length > 1 && (
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {frames.map((_, i) => <button key={i} onClick={() => setFrame(i)} className={`w-1.5 h-1.5 rounded-full ${i === frame ? 'bg-amber-400' : 'bg-neutral-600'}`} />)}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Eye className="w-3 h-3" /> Vorschau: {board.preview_kind === 'live' ? 'Live-Daten' : 'Beispiel (PROJECT SAMPLE)'}</p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button size="sm" disabled={!!busy} data-testid={`vb-push-${board.id}`}
              onClick={() => run('push', () => api.vbManagerPush(board.id), (d) => d.status === 'gate_wait' ? `Push eingereiht — Note erlaubt nur ein Update alle 20 s (in ${d.retry_in}s)` : `${board.name} wird ${minutesLabel(d.display_seconds)} gezeigt${d.sample ? ' (Beispiel)' : ''}${d.status === 'dry_run' ? ' — DRY_RUN' : ''}`)}
              className="h-7 text-[11px] bg-amber-600 hover:bg-amber-500 text-white">
              {busy === 'push' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} Jetzt anzeigen
            </Button>
            {board.kind === 'event' && board.has_simulate && (
              <Button size="sm" variant="outline" disabled={!!busy} data-testid={`vb-simulate-${board.id}`}
                onClick={() => run('sim', () => api.vbManagerSimulate(board.id), (d) => d.status === 'board_disabled' ? 'Board ist deaktiviert' : 'Simuliertes Event (PROJECT SAMPLE) in die Warteschlange gestellt')}
                className="h-7 text-[11px]">
                {busy === 'sim' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FlaskConical className="w-3 h-3 mr-1" />} Simulieren
              </Button>
            )}
            <button onClick={() => setOpen(o => !o)} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid={`vb-toggle-settings-${board.id}`}>
              Einstellungen {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
        </div>
      </div>
      {open && <BoardSettingsForm board={board} onSaved={() => { setOpen(false); onChanged(); }} />}
    </div>
  );
};
